import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SubTenant } from '../models/subtenant.model';

const SUBTENANT_TOKEN_KEY = 'subtenant_token';
const SUBTENANTS_EMPTY_KEY = 'subtenants_empty';

@Injectable({ providedIn: 'root' })
export class SubTenantService {
    private readonly endpoint: string = environment.apiUrl;

    readonly subtenantToken = signal<string | null>(sessionStorage.getItem(SUBTENANT_TOKEN_KEY));
    readonly subtenantsChecked = signal<boolean>(
        !!sessionStorage.getItem(SUBTENANT_TOKEN_KEY) ||
        sessionStorage.getItem(SUBTENANTS_EMPTY_KEY) === 'true'
    );
    readonly hasSubtenants = signal<boolean>(!!sessionStorage.getItem(SUBTENANT_TOKEN_KEY));

    constructor(private http: HttpClient) {}

    getSubtenants(): Observable<SubTenant[]> {
        return this.http.get<SubTenant[]>(`${this.endpoint}/user/subtenants`);
    }

    selectSubtenant(subtenantId: string): Observable<string> {
        return this.http.post(
            `${this.endpoint}/user/select-subtenant`,
            { subtenantId },
            { responseType: 'text' }
        ).pipe(
            tap((response: string) => {
                let token = response;
                try {
                    const parsed = JSON.parse(response);
                    if (parsed && typeof parsed.token === 'string') {
                        token = parsed.token;
                    }
                } catch {}
                this.subtenantToken.set(token);
                this.subtenantsChecked.set(true);
                this.hasSubtenants.set(true);
                sessionStorage.setItem(SUBTENANT_TOKEN_KEY, token);
                sessionStorage.removeItem(SUBTENANTS_EMPTY_KEY);
            })
        );
    }

    markNoSubtenants(): void {
        this.subtenantsChecked.set(true);
        this.hasSubtenants.set(false);
        sessionStorage.setItem(SUBTENANTS_EMPTY_KEY, 'true');
    }

    setAvailableSubtenants(subtenants: SubTenant[]): void {
        this._availableSubtenants = subtenants;
    }

    getAvailableSubtenants(): SubTenant[] {
        return this._availableSubtenants;
    }

    private _availableSubtenants: SubTenant[] = [];

    clearSubtenant(): void {
        this.subtenantToken.set(null);
        this.subtenantsChecked.set(false);
        this.hasSubtenants.set(false);
        this._availableSubtenants = [];
        sessionStorage.removeItem(SUBTENANT_TOKEN_KEY);
        sessionStorage.removeItem(SUBTENANTS_EMPTY_KEY);
    }
}
